import {
  COMPLIMENTARY_STATUS,
  type BillingInterval,
  type CheckoutChoice,
  type PaidPlanId,
  type PlanChangePreview,
} from '@lyrmo/shared';
import { useState } from 'react';
import { useI18n } from '../../i18n/i18n-context';
import { useTrack } from '../../lib/tracking-context';
import { useAccount } from './account-context';

/** Where choosing a plan has got to: the plans, signing in first, or confirming a change. */
export type PlanStep = 'plans' | 'sign-in' | 'change';

/** The state of choosing a plan, kept by whatever shows it, so it can title what it shows. */
export function usePlanChooser() {
  const { t, describeError } = useI18n();
  const { client, account, planChangesEnabled, checkout, changePlan } = useAccount();
  const track = useTrack();
  const [signInFor, setSignInFor] = useState<PaidPlanId | null>(null);
  const [chosenInterval, setBillingInterval] = useState<BillingInterval | null>(null);
  const [change, setChange] = useState<{
    choice: CheckoutChoice;
    preview: PlanChangePreview | null;
    previewError: string | null;
  } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const user = account?.user ?? null;
  const current = account?.plan ?? 'free';
  // A lapsed subscription doesn't say how the current plan is billed.
  const subscription = account?.subscription?.plan === current ? account.subscription : null;
  const currentInterval = subscription?.interval ?? null;
  // Subscribers see their own billing interval first.
  const interval = chosenInterval ?? currentInterval ?? 'month';
  // Signing in finishes in the tab opened from the email; once it shows up, offer the plans.
  const signingInFor = user ? null : signInFor;
  const step: PlanStep = signingInFor ? 'sign-in' : change ? 'change' : 'plans';

  /** Back to the plans, as they first were. */
  function reset() {
    setSignInFor(null);
    setBillingInterval(null);
    setChange(null);
    setError(null);
  }

  async function run(action: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (caught) {
      setError(describeError(caught));
    } finally {
      setBusy(false);
    }
  }

  /** Shows what switching to `choice` costs now, as the payment provider works it out. */
  function reviewChange(choice: CheckoutChoice) {
    setChange({ choice, preview: null, previewError: null });
    setError(null);
    // Either way, unless they went back, or chose another plan, in the meantime.
    client.previewPlanChange(choice).then(
      (preview) => {
        setChange((shown) => (shown?.choice === choice ? { ...shown, preview } : shown));
      },
      (caught: unknown) => {
        const previewError = describeError(caught);
        setChange((shown) => (shown?.choice === choice ? { ...shown, previewError } : shown));
      },
    );
  }

  function choose(plan: PaidPlanId) {
    if (!planChangesEnabled) return;
    track({ event: 'plan_chosen', plan, interval });
    if (!user) {
      // Signing in comes first, in the plans.
      track({ event: 'sign_in_opened' });
      setSignInFor(plan);
      return;
    }
    // A plan given for free has no subscription to switch: subscribing takes over from it.
    const subscribing = current === 'free' || subscription?.status === COMPLIMENTARY_STATUS;
    if (subscribing) void run(() => checkout({ plan, interval }));
    // Subscribers see the prorated price before switching.
    else reviewChange({ plan, interval });
  }

  /** Switches plans, then `done`, such as closing the dialog. */
  function confirmChange(choice: CheckoutChoice, done: () => void) {
    if (!planChangesEnabled) return;
    void run(async () => {
      await changePlan(choice);
      done();
    });
  }

  const titles: Readonly<Record<PlanStep, string>> = {
    plans: t.plans.title,
    'sign-in': t.plans.signInTitle,
    change: t.plans.changeTitle,
  };

  return {
    step,
    title: titles[step],
    interval,
    current,
    currentInterval,
    signingInFor,
    change,
    busy,
    error,
    user,
    reset,
    run,
    choose,
    confirmChange,
    setBillingInterval,
    setSignInFor,
    setChange,
    setError,
  };
}

export type PlanChooserState = ReturnType<typeof usePlanChooser>;
